/** 活动独立选品/排序编辑；回落集合只读呈现，绝不自动写成活动配置。 */
import type { PaymentPageProductOrderItem } from '@novel-admin/shared';
import { onlineManager, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AdminNetworkError, AdminRequestAbortedError } from '~/api/errors';
import { paymentOffersApi } from '~/hooks/use-payment-offers';
import { refillSortableSlots } from '~/lib/reorder';
import { useAuthStore } from '~/stores/auth-store';
import { Dialog } from '~/ui/Dialog';
import { SortableList } from '~/ui/SortableList';
import { toast } from '~/ui/Toast';
import { PaymentProductScopeHints } from '../PaymentProductScopeHints';

/** 失效项提示块：既有 card-flat + warn 软底色（同 chip-warn 配色），不另造视觉 */
const WARN_NOTICE_STYLE = {
  padding: '8px 12px',
  background: 'var(--warn-soft)',
  borderColor: 'transparent',
  color: 'var(--warn)',
  fontSize: 'var(--text-xs)',
} as const;

export function PaymentOfferProductOrderDialog({
  offerId,
  appId,
  platform,
  onClose,
}: {
  offerId: string;
  appId: string;
  platform: string;
  onClose: () => void;
}) {
  const role = useAuthStore((state) => state.user?.role);
  const canWrite = role === 'operator' || role === 'admin' || role === 'root';
  const queryClient = useQueryClient();
  const queryKey = ['payment-offer-product-order', offerId];
  const query = useQuery({
    queryKey,
    queryFn: () => paymentOffersApi.getProductOrder(offerId),
    refetchOnMount: 'always',
  });
  const [draft, setDraft] = useState<string[] | null>(null);
  const response = query.data;
  const knownIds = new Set(response?.items.map((item) => item.access_level_id) ?? []);
  const initial = response?.is_configured ? response.effective_access_level_ids : [];
  const selected = draft ?? initial;
  const invalidIds = selected.filter((id) => !knownIds.has(id));
  // 缓存只供展示；本次打开必须成功重读当前集合，才可编辑整组替换。
  const hasFreshRead =
    query.isFetchedAfterMount && !query.isFetching && !query.isError && !!response;
  const save = useMutation({
    // 整组替换不能排队到重连后覆盖其他运营的新配置；显式覆盖全局网络模式和重试设置。
    networkMode: 'always',
    retry: false,
    mutationFn: (ids: string[]) => {
      if (!onlineManager.isOnline())
        throw new Error('网络不可用，本次保存未发送；恢复后请重新确认并保存。');
      return paymentOffersApi.replaceProductOrder(offerId, { access_level_ids: ids });
    },
    onSuccess: async (result) => {
      queryClient.setQueryData(queryKey, result);
      await queryClient.invalidateQueries({ queryKey: ['payment-offers', appId] });
      toast('已保存活动上架集合与顺序', { tone: 'pos', icon: 'IconCheck' });
      onClose();
    },
    onError: (error) => {
      // 发出的写请求丢失响应时无法证明未提交；不重试，也不承诺已撤销。
      const resultUnknown =
        error instanceof AdminNetworkError ||
        (error instanceof AdminRequestAbortedError && error.serverStateUnknown);
      toast(
        resultUnknown
          ? '保存结果未知，请刷新确认后再提交。'
          : error instanceof Error
            ? error.message
            : '保存失败',
        {
          tone: 'neg',
          icon: 'IconAlert',
        },
      );
    },
  });
  const canEdit = canWrite && hasFreshRead && !save.isPending;
  const updateSelection = (next: string[]) => {
    if (canEdit) setDraft(next);
  };
  /** 板块内重排（上移 / 下移 / 拖拽都汇到这里）：用新序列按原槽位回填，另一板块的相对次序不变 */
  const reorderSection = (isInSection: (id: string) => boolean, sectionIds: string[]) => {
    if (!canEdit) return;
    if (selected.filter(isInSection).length !== sectionIds.length) return;
    updateSelection(refillSortableSlots(selected, isInSection, sectionIds));
  };
  /** 板块：订阅恒在前；已上架项按 selected 序在前（可排序），未上架项补在后（不可排序） */
  const sections = [
    { label: '订阅产品区', isIn: (type: string) => type === 'SUBSCRIPTION' },
    { label: '充值产品区', isIn: (type: string) => type !== 'SUBSCRIPTION' },
  ].map((section) => {
    const items = (response?.items ?? []).filter((item) => section.isIn(item.product_type));
    const position = (item: PaymentPageProductOrderItem) => selected.indexOf(item.access_level_id);
    return {
      label: section.label,
      isIn: section.isIn,
      items: [...items].sort(
        (left, right) =>
          (position(left) < 0 ? Number.MAX_SAFE_INTEGER : position(left)) -
          (position(right) < 0 ? Number.MAX_SAFE_INTEGER : position(right)),
      ),
    };
  });
  const typeById = new Map(
    (response?.items ?? []).map((item) => [item.access_level_id, item.product_type]),
  );
  return (
    <Dialog
      title="活动上架商品与顺序"
      onClose={() => {
        if (!save.isPending) onClose();
      }}
      footer={
        <>
          {/* 按钮靠右，与其它弹窗页脚一致 */}
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost" disabled={save.isPending} onClick={onClose}>
            关闭
          </button>
          <button
            className="btn btn-primary"
            disabled={!canEdit || invalidIds.length > 0}
            onClick={() => {
              if (!canEdit || invalidIds.length > 0) return;
              if (!onlineManager.isOnline()) {
                toast('网络不可用，本次保存未发送；恢复后请重新确认并保存。', {
                  tone: 'neg',
                  icon: 'IconAlert',
                });
                return;
              }
              save.mutate(selected);
            }}
          >
            {save.isPending ? '保存中…' : '保存'}
          </button>
        </>
      }
    >
      {/* Dialog 壳 body 不带内边距，由弹窗自带；与「新建 App」等弹窗同一套 18px 左右边距 */}
      <div
        className="vstack"
        style={{ gap: 12, padding: '14px 18px 18px', fontSize: 'var(--text-sm)' }}
      >
        {query.isLoading ? (
          <p>正在读取商品…</p>
        ) : query.isError ? (
          <p role="alert">读取失败，请重试。</p>
        ) : (
          response && (
            <>
              {query.isFetching && <p>正在更新商品，读取完成后可编辑。</p>}
              <div
                className="card-flat"
                style={{ padding: 12, background: 'var(--bg-subtle)', fontSize: 'var(--text-xs)' }}
              >
                <strong>
                  {
                    { OFFER: '活动独立集合', SCENARIO: '沿用场景集合', NONE: '未配置、C 端空态' }[
                      response.effective_source
                    ]
                  }
                </strong>
                <div>
                  当前有效集合 {response.effective_product_count} 个：
                  {response.effective_access_level_ids.join('、') || '无'}
                </div>
                <div>
                  下方只编辑活动独立集合。清空并保存后沿用场景集合；场景也未配置时 C
                  端该活动页空态。
                </div>
              </div>
              <PaymentProductScopeHints
                appId={appId}
                scenario={response.scenario}
                platform={platform}
                items={response.items}
                selectedAccessLevelIds={draft ?? response.effective_access_level_ids}
              />
              {!response.is_configured &&
                response.effective_access_level_ids
                  .filter((id) => !knownIds.has(id))
                  .map((id) => (
                    <div className="card-flat" style={WARN_NOTICE_STYLE} key={id}>
                      场景集合失效项：{id}，请在场景页面清理。
                    </div>
                  ))}
              {invalidIds.map((id) => (
                <div
                  className="card-flat hstack"
                  style={{ ...WARN_NOTICE_STYLE, justifyContent: 'space-between' }}
                  key={id}
                >
                  失效项：{id}
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={!canEdit}
                    onClick={() => updateSelection(selected.filter((value) => value !== id))}
                  >
                    移除
                  </button>
                </div>
              ))}
              <button
                className="btn btn-ghost"
                style={{ alignSelf: 'flex-start' }}
                disabled={!canEdit || selected.length === 0}
                onClick={() => updateSelection([])}
              >
                清空活动独立集合
              </button>
              {sections.map((section) =>
                section.items.length === 0 ? null : (
                  <section key={section.label}>
                    <div className="label" style={{ marginBottom: 6 }}>
                      {section.label}
                    </div>
                    <SortableList<PaymentPageProductOrderItem>
                      items={section.items}
                      getKey={(item) => item.access_level_id}
                      itemLabel={(item) => item.name}
                      isSortable={(item) => selected.includes(item.access_level_id)}
                      disabled={!canEdit}
                      onReorder={(next) =>
                        reorderSection(
                          (id) => section.isIn(typeById.get(id) ?? ''),
                          next
                            .filter((item) => selected.includes(item.access_level_id))
                            .map((item) => item.access_level_id),
                        )
                      }
                      renderContent={(item) => (
                        <>
                          <div style={{ fontWeight: 500 }}>{item.name}</div>
                          <div className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>
                            {item.access_level_id}
                          </div>
                          {!item.is_in_discovery_scope && <div>不属于本页发现面，C 端不展示</div>}
                          {!item.is_serveable && <div>暂不可售</div>}
                        </>
                      )}
                      renderActions={(item) => (
                        <input
                          type="checkbox"
                          aria-label={`上架 ${item.access_level_id}`}
                          checked={selected.includes(item.access_level_id)}
                          disabled={!canEdit}
                          onChange={(event) =>
                            updateSelection(
                              event.target.checked
                                ? [...selected, item.access_level_id]
                                : selected.filter((id) => id !== item.access_level_id),
                            )
                          }
                        />
                      )}
                    />
                  </section>
                ),
              )}
            </>
          )
        )}
      </div>
    </Dialog>
  );
}
